import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { isSelected } from 'helpers';
import { useDebounce } from 'hooks';
import { ruLocale } from 'lang/ruLocale';
import { addLocale, FilterMatchMode, locale } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import {   DataTable,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortMeta,
  DataTableStateEvent } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { MeasureUnitUI } from 'types/interfaces/MeasureUnitUI';
import { clearFilter } from 'utils';
import VisibilityWrapper from 'utils/wrappers/VisibilityWrapper';

import { MeasureUnitService } from 'components/services/MeasureUnitService';
import { MeasureUnitTemplate } from 'constants/MeasureUnitTemplate';
import SearchInputWithClear from 'ui/custom-controls/SearchInputWithClear';
import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import TaskMeasureUnitCard from 'ui/TaskMeasureUnitCard';

export default function MeasureUnit() {
  addLocale('ru', ruLocale.ru);
  locale('ru');

  
  const [measureUnits, setMeasureUnits] = useState<MeasureUnitUI[]>([]);
  const [selectedMeasureUnits, setSelectedMeasureUnits] = useState<MeasureUnitUI>(MeasureUnitTemplate);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState(100);
  const [pageNumber, setPageNumber] = useState(1);
  const [first, setFirst] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false);

  const [filters, setFilters] = useState<Record<string, DataTableFilterMetaData>>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    description: { value: null, matchMode: FilterMatchMode.CONTAINS },
    shortName: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });

  const nameFilterRef = useRef<HTMLInputElement>(null);
  const descriptionFilterRef = useRef<HTMLInputElement>(null);
  const shortNameFilterRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);
  const isSelectedIdExist = isSelected(selectedMeasureUnits);
  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);

  const measureUnitRef = useRef<null | { handleCancel: () => void }>(null);


  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setMultiSortMeta(e.multiSortMeta ?? null);
  }, []);

  const onPageChanged = useCallback((event: DataTablePageEvent) => {
    if (event.rows !== undefined) {
      setPageSize(event.rows);
      setFirst(0);
    }
    if (event.page !== undefined) {
      setFirst(event.first);
      setPageNumber(event.first / event.rows + 1);
    }
  }, []);


  const loadData = useCallback(() => {
    const sort = multiSortMeta?.[0]; 
    const payload = JSON.stringify([
      { field: 'name', value: filters.name?.value, operator: filters.name?.matchMode },
      { field: 'description', value: filters.description?.value, operator: filters.description?.matchMode },
      { field: 'shortName', value: filters.shortName?.value, operator: filters.shortName?.matchMode }, 
      { field: 'global', value: filters.global?.value, operator: filters.global?.matchMode }
    ]);

    const service = MeasureUnitService.getMeasureUnit({
      pageNumber,
      pageSize,
      sortBy: sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: payload
    });

    service.then(res => {
      if (res.data.isSuccess) {
        setMeasureUnits(res.data.value.itemsList);
        setTotalCount(res.data.value.total || 0);
      }
    });
  }, [filters, multiSortMeta, pageNumber, pageSize]);

  const { debouncedCallback: debouncedLoadData } = useDebounce(loadData, 300);

  useEffect(() => {
    debouncedLoadData();
  }, [filters, multiSortMeta, pageNumber, pageSize]);


  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    const next = e.filters as Record<string, DataTableFilterMetaData>;
    setFilters(next);
  }, []);

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const _filters = JSON.parse(JSON.stringify(filters));
    _filters[filter].value = value;
    setFilters(_filters);
  };

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleInputChange =
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    };



  const addButtonHandle = () => {
    setSelectedMeasureUnits(MeasureUnitTemplate);
    setDialogVisible(true);
  };  

  const renderHeader = () => {
    return (
      <div id="table-header" className="flex justify-content-between">
        <SearchInputWithClear
          onChange={(value) => {
            const mockEvent = {
              target: { value }
            } as React.ChangeEvent<HTMLInputElement>;
            handleInputChange('global')(mockEvent);
          }}
          placeholder="Поиск по ключевым словам"
        />
        <span className="flex align-items-center gap-2">
          <Button
            className="measureUnits-add-btn__roles"
            type="button"
            icon="pi pi-plus"
            severity="success"
            rounded
            onClick={() => {
              addButtonHandle();
            }}
          />
          <Button
            className="measureUnits-edit-btn__roles"
            type="button"
            icon="pi pi-pencil"
            rounded
            onClick={() => {
              if (toast.current && !isSelectedIdExist) {
                toast.current.show({
                  severity: 'warn',
                  summary: 'Ошибка',
                  detail: 'Выберите единицу измерения для редактирования',
                  life: 3000
                });
              }
              if (isSelectedIdExist) setDialogVisible(true);
            }}
          />
          <Button
            className="measureUnits-delete-btn__roles"
            type="button"
            icon="pi pi-trash"
            severity="danger"
            rounded
            onClick={() => {
              if (selectedMeasureUnits?.id) {
                setDeleteDialogVisible(true);
              }
              if (toast.current && !selectedMeasureUnits?.id) {
                toast.current.show({
                  severity: 'warn',
                  summary: 'Ошибка',
                  detail: 'Выберите единицу измерения для удаления',
                  life: 3000
                });
              }
            }}
          />
        </span>
      </div>
    );
  };

  const header = renderHeader();

  const tableElement = document.querySelector('.p-datatable-thead');

  function offset(el: Element) {
    var rect = el.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    const tableOffset =
      tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
    return `calc(100vh - ${
      tableElement instanceof HTMLElement ? tableOffset + paginatorHeight : 0
    }px)`;
  }, [tableElement]);

  const onHideDetailMeasureUnit = () => {
    if (measureUnitRef.current !== null) {
      measureUnitRef.current.handleCancel();
    }
  };

  const handleUpdate = (id: number, MeasureUnit: MeasureUnitUI) =>
    MeasureUnitService.updateMeasureUnit(id, MeasureUnit).then(res => {
      loadData();
      return res;
    });

  const handleAdd = (MeasureUnit: MeasureUnitUI) =>
    MeasureUnitService.createMeasureUnit(MeasureUnit).then(res => {
      loadData();
      return res;
    });

  const closeDialog = () => {
    setDialogVisible(false);
  };

  return (
    <div className="measure-measureUnits-table wrapper wrapper--main">
      <Toast ref={toast} />
 
      <Dialog
        header="Настройка единицы измерения"
        visible={dialogVisible}
        style={{ 
          minWidth: '630px', 
          // width: '630px',
          // height: '500px',
          minHeight: 380 
        }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
        maximizable
        resizable 
        modal
        contentStyle={{ 
          height: 'calc(100% - 45px)',
          overflow: 'hidden', 
        }}
        onHide={() => onHideDetailMeasureUnit()}
      >
        <div style={{ height: '100%',}}>
          <VisibilityWrapper code="measureUnit-card__roles">
            <TaskMeasureUnitCard
              taskMeasureUnitCardProps={selectedMeasureUnits}
              onUpdate={handleUpdate}
              onDelete={id => {
                MeasureUnitService.deleteMeasureUnit(Number(id)).then(() => loadData());
              }}
              onAdd={handleAdd}
              closeDialog={closeDialog}
              ref={measureUnitRef}
            />
          </VisibilityWrapper>
        </div>
      </Dialog>    
      
      <DeleteConfirmationDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirm={() => {MeasureUnitService.deleteMeasureUnit(selectedMeasureUnits.id).then(() => loadData())}}
        entityType="единицу измерения"
        entityName={selectedMeasureUnits?.name}
      />
      
      <DataTable
        filters={filters}
        lazy
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        selectionMode="single"
        selection={selectedMeasureUnits!}
        onSelectionChange={(e) => setSelectedMeasureUnits(e.value)}
        onRowDoubleClick={(e) => {
          setDialogVisible(true);
          setSelectedMeasureUnits(e.data as MeasureUnitUI);
        }}
        metaKeySelection={false}
        scrollable
        paginator
        first={first}
        rows={pageSize}
        totalRecords={totalCount}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPage={onPageChanged}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink JumpToPageInput"
        currentPageReportTemplate="{currentPage} из {totalPages}"
        paginatorLeft={' '}
        paginatorRight={`Всего: ${measureUnits.length > 0 ? totalCount : 0}`}
        sortMode="multiple"
        removableSort
        scrollHeight={scrollHeight}
        size="small"
        resizableColumns
        reorderableColumns
        reorderableRows
        value={measureUnits}
        dataKey="id"
        filterDisplay="row"
        header={header}
        emptyMessage="Единицы измерения не найдены."
        onRowReorder={(e) => setMeasureUnits(e.value)}
        tableStyle={{ minWidth: '50rem' }}
        onFilter={onFiltersChange}
      >
        <Column
          columnKey="name"
          filter
          sortable
          field="name"
          header="Название"
          style={{ minWidth: '1rem', width: '30%' }}
          onFilterClear={() =>
            clearFilter('name', filters, setFilters, nameFilterRef)
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={nameFilterRef}
                onChange={handleInputChange('name')}
                placeholder="Поиск по названию"
              />
            </div>
          }
        />
        <Column
          columnKey="shortName"
          filter
          sortable
          field="shortName"
          header="Короткое название"
          style={{ minWidth: '1rem', width: '17%' }}
          onFilterClear={() =>
            clearFilter(
              'shortName',
              filters,
              setFilters,
              shortNameFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={shortNameFilterRef}
                onChange={handleInputChange('shortName')}
                placeholder="Поиск по короткому названию"
              />
            </div>
          }
        />
        <Column
          columnKey="description"
          filter
          sortable
          field="description"
          header="Описание"
          style={{ minWidth: '1rem', width: '30%' }}
          onFilterClear={() =>
            clearFilter(
              'description',
              filters,
              setFilters,
              descriptionFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={descriptionFilterRef}
                onChange={handleInputChange('description')}
                placeholder="Поиск по описанию"
              />
            </div>
          }
        />

      </DataTable> 
    </div>
  );
}
